import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

/**
 * Subscribes to backend `entity:changed` events for a pedigree.
 * The backend emits this from any write path that mutates clinical
 * entities (genotype, phenotype, test, …) so frontend caches can
 * re-fetch on demand instead of polling.
 *
 * Joins the `pedigree:${familyId}` room — the same room used by
 * `usePersonLocks`/`usePedigreeLock` — so concurrent clients of the
 * same family receive each other's signals plus self-originated
 * server-side mutations (e.g. test-save → auto-propagation).
 *
 * @param {Object} args
 * @param {string} args.familyId          required to join the room
 * @param {string[]} [args.entityTypes]   filter — only call onChange
 *                                        when payload.entityType is included.
 *                                        Omit to receive all types.
 * @param {Function} args.onChange        (payload) => void
 *
 *   payload shape: {
 *     familyId, entityType, entityId, familyMemberId, source, at
 *   }
 */
export function useEntityChangeSubscription({
  familyId,
  entityTypes,
  onChange
}) {
  // Hold the latest onChange in a ref so we can listen with a stable
  // handler and avoid tearing down the socket on every parent render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!familyId) return undefined;

    const socket = io('', { transports: ['websocket', 'polling'] });

    socket.on('connect', () => {
      socket.emit('pedigree:join', { familyId });
    });

    const handler = (payload) => {
      if (!payload) return;
      if (
        Array.isArray(entityTypes) &&
        entityTypes.length > 0 &&
        !entityTypes.includes(payload.entityType)
      ) {
        return;
      }
      try {
        onChangeRef.current?.(payload);
      } catch (err) {
        console.warn('useEntityChangeSubscription handler threw:', err);
      }
    };

    socket.on('entity:changed', handler);

    return () => {
      try {
        socket.emit('pedigree:leave', { familyId });
      } catch (_) {
        /* noop */
      }
      socket.off('entity:changed', handler);
      socket.disconnect();
    };
    // entityTypes is an array; passing it directly would cause re-subs on
    // every render. Stringify for stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, JSON.stringify(entityTypes || [])]);
}
